import { useImageUrl } from '@/hooks/useFile'
import { showError } from '@/utils/toast'
import { ImagePlus, X } from 'lucide-react'
import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

export function ImageUpload({
  files,
  imageIds = [],
  imageUrls = [],
  onChange,
  onRemoveExisting,
  maxImages = 5,
  readonly = false,
  testId,
  accept = 'image/*',
}: {
  files: File[]
  imageIds?: string[]
  imageUrls?: string[]
  onChange: (files: File[]) => void
  onRemoveExisting?: (index: number) => void
  maxImages?: number
  readonly?: boolean
  testId?: string
  accept?: string
}) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)

  const totalImages = imageIds.length + files.length
  const uploadText =
    maxImages > 1
      ? `${t('common.add')} ${t('common.image')} (${totalImages}/${maxImages})`
      : `${t('common.add')} ${t('common.image')}`

  const handleFiles = useCallback(
    (fileList: FileList) => {
      const acceptedFiles = Array.from(fileList).filter(file => {
        if (accept === '*/*') return true
        const acceptPrefix = accept.replace(/\/\*$/, '/')
        return file.type.startsWith(acceptPrefix)
      })

      if (acceptedFiles.length < fileList.length) {
        showError(t('common.invalidFileType'))
      }

      if (acceptedFiles.length === 0) {
        return
      }

      const remaining = maxImages - (imageIds.length + files.length)
      const filesToProcess = acceptedFiles.slice(0, remaining)

      onChange([...files, ...filesToProcess])
    },
    [files, imageIds.length, maxImages, onChange, accept, t],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files)
    },
    [handleFiles],
  )

  return (
    <div className="flex flex-col gap-3">
      {(files.length > 0 || imageUrls.length > 0 || imageIds.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {imageIds.map((id, i) => (
            <ImagePreview key={id} imageId={id} readonly={readonly} onRemove={() => onRemoveExisting?.(i)} testId={testId ? `${testId}-remove` : undefined} />
          ))}
        </div>
      )}

      {!readonly && totalImages < maxImages && (
        <div
          data-testid={testId ? `${testId}-dropzone` : undefined}
          className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border/50 p-4 text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
          onClick={() => inputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
        >
          <ImagePlus className="size-4" />
          <span className="text-xs">{uploadText}</span>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple={maxImages > 1}
            className="hidden"
            data-testid={testId}
            onChange={e => e.target.files && handleFiles(e.target.files)}
          />
        </div>
      )}
    </div>
  )
}

function ImagePreview({ imageId, readonly, onRemove, testId }: { imageId: string; readonly: boolean; onRemove?: () => void; testId?: string }) {
  const { data: imageUrl, isLoading } = useImageUrl(imageId)

  return (
    <div className="group relative size-20 overflow-hidden rounded-lg border border-border bg-secondary/40">
      {imageUrl ? <img src={imageUrl} className="size-full object-cover" /> : null}
      {isLoading ? <div className="size-full animate-pulse bg-secondary" /> : null}
      {!readonly && (
        <button
          type="button"
          data-testid={testId}
          onClick={onRemove}
          className="absolute inset-0 flex items-center justify-center bg-background/60 opacity-0 transition-opacity group-hover:opacity-100"
        >
          <X className="size-4 text-muted-foreground" />
        </button>
      )}
    </div>
  )
}
